import React, { useMemo, useState, useRef, useCallback } from "react";
import { AgGridReact } from "ag-grid-react";
// @ts-ignore CSS is loaded by the application bundler.
import "ag-grid-community/styles/ag-grid.css";
// @ts-ignore CSS is loaded by the application bundler.
import "ag-grid-community/styles/ag-theme-alpine.css";
import SearchBar from "../../SearchBarTable/SearchBarTable";
import { FaCubes, FaGlobeAmericas } from "react-icons/fa";
// @ts-ignore CSS is loaded by the application bundler.
import "../AgGridTheme.css";
import SortHeader from "../SortingHeader";

import {
  ClientSideRowModelModule,
  TextFilterModule,
  PaginationModule,
  ModuleRegistry
} from "ag-grid-community";

ModuleRegistry.registerModules([
  ClientSideRowModelModule,
  TextFilterModule,
  PaginationModule,
]);

interface MetricGroupTableProps {
  metricGroups: any[];
  onRowSelect: (metricGroup: any) => void;
  onClearSelection: () => void;
  selectedRow: string | number | null;
}

const MetricGroupTable: React.FC<MetricGroupTableProps> = ({
  metricGroups,
  onRowSelect,
  onClearSelection,
  selectedRow,
}) => {
  const [searchQuery, setSearchQuery] = useState("");
  const gridRef = useRef<AgGridReact>(null);

  const handleSearchChange = (q: string) => setSearchQuery(q);

  // Filter logic
  const filteredMetricGroups = useMemo(() => {
    const s = searchQuery.toLowerCase();
  
    return metricGroups.filter((group) => {
      const name = group.metric_group_name ?? "";
      const iso = group.iso_code ?? "";
  
      const nameMatch = name.toString().toLowerCase().includes(s);
      const isoMatch = iso.toString().toLowerCase().includes(s);
  
      const metricsMatch =
        Array.isArray(group.metric_ids) &&
        group.metric_ids.some((m: any) => {
          const metricName = m?.metric_name ?? "";
          return metricName.toString().toLowerCase().includes(s);
        });
  
      return nameMatch || isoMatch || metricsMatch;
    });
  }, [metricGroups, searchQuery]);
  

  // Column definitions
  const columnDefs = useMemo(
    () => [
      {
        headerName: "Metric Group Name",
        field: "metric_group_name",
        sortable: true,
        flex: 1,
        minWidth: 260,
        cellRenderer: (params: any) => (
          <div className="flex items-center gap-2">{params.value}</div>
        ),
        headerComponent: SortHeader,
        headerComponentParams: {
          displayName: "Metric Group Name",
          enableSorting: true,
          icon: <FaCubes className="text-gray-600" />,
        },
        headerClass:
          "p-4 text-left text-base font-bold text-gray-700 bg-gray-200 cursor-pointer",
      },

      {
        headerName: "ISO Code",
        field: "iso_code",
        sortable: true,
        flex: 1,
        minWidth: 150,
        cellRenderer: (params: any) => (
          <div className="flex items-center gap-2">{params.value}</div>
        ),
        headerComponent: SortHeader,
        headerComponentParams: {
          displayName: "ISO Code",
          enableSorting: true,
          icon: <FaGlobeAmericas className="text-gray-600" />,
        },
        headerClass:
          "p-4 text-left text-base font-bold text-gray-700 bg-gray-200 cursor-pointer",
      },

      {
        headerName: "Metrics",
        field: "metric_ids",
        sortable: true,
        flex: 1,
        minWidth: 300,
        autoHeight: true,
        cellRenderer: (params: any) => {
          const metrics = params.data.metric_ids;
          if (!Array.isArray(metrics) || metrics.length === 0) return "N/A";

          const rows = [];
          for (let i = 0; i < metrics.length; i += 5) {
            rows.push(metrics.slice(i, i + 5));
          }

          return (
            <div className="whitespace-normal break-words py-1">
              {rows.map((row, idx) => (
                <div key={idx} className="flex flex-wrap gap-x-1">
                  {row.map((m: any, i: number) => (
                    <span key={i}>
                      {m.metric_name}
                      {i < row.length - 1 ? ", " : ""}
                    </span>
                  ))}
                </div>
              ))}
            </div>
          );
        },
        headerComponent: SortHeader,
        headerComponentParams: {
          displayName: "Metrics",
          enableSorting: true,
          icon: <FaCubes className="text-gray-600" />,
        },
        headerClass:
          "p-4 text-left text-base font-bold text-gray-700 bg-gray-200 cursor-pointer",
      }
    ],
    []
  );

  const defaultColDef = useMemo(
    () => ({
      resizable: true,
      cellStyle: {
        padding: "8px 12px",
        fontSize: "16px",
        fontFamily: "'Poppins', sans-serif",
        color: "#374151",
      },
    }),
    []
  );

  // On row select
  const handleRowClicked = useCallback(
    (event: any) => {
      onRowSelect(event.data);
    },
    [onRowSelect]
  );

  return (
    <div className="p-4 bg-white shadow-md rounded-xl font-poppins">
      {/* Search */}
      <div className="flex justify-between items-center mb-4">
        <div className="flex items-center bg-gray-200 px-3 py-2 rounded-full max-w-md">
          <SearchBar
            searchQuery={searchQuery}
            onSearchChange={handleSearchChange}
            placeholder="Search metric groups..."
          />
        </div>
      </div>

      {/* Table */}
      <div
        className="ag-theme-alpine font-poppins text-base text-gray-700"
        style={{
          height: "var(--admin-tab-grid-height, max(475px, calc(100dvh - 320px)))",
          width: "100%",
          overflow: "auto",
          border: "1px solid #E7E4FF",
          borderRadius: "8px",
        }}
      >
        <AgGridReact
          theme="legacy"
          ref={gridRef}
          rowData={filteredMetricGroups}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          rowSelection={{ mode: 'singleRow' }}
          rowHeight={60}
          headerHeight={55}
          pagination={true}
          paginationPageSize={15}
          paginationPageSizeSelector={[15, 20, 50, 100]}
          suppressRowHoverHighlight={false}
          animateRows
          onRowClicked={handleRowClicked}
          domLayout="normal"
          getRowClass={(params) =>
            params.data.metric_group_id === selectedRow ? "bg-gray-200" : ""
          }
          getRowHeight={(params) => {
            const count = params.data?.metric_ids?.length || 0;
            const lines = Math.ceil(count / 5);
            const baseHeight = 60;
            const lineHeight = 24;
            return baseHeight + (lines - 1) * lineHeight;
          }}
        />
      </div>
    </div>
  );
};

export default MetricGroupTable;
